/**
 * Anonymisation de documents médicaux
 * Même principe [code] partout : chaque donnée sensible est remplacée par un code entre crochets.
 * - Patient : variante du nom → [01Z4-01], [01Z4-02], ... (une par déclinaison)
 * - Docteur : [DOCTEUR]
 * - Orthoptiste : [ORTHOPTISTE]
 * - Email / Téléphone / Adresse : [EMAIL], [TELEPHONE], [ADRESSE]
 */

export interface CorrespondenceEntry {
  /** Variante du nom (ex. "LE GUEHENNEC ROSE", "Rose") */
  original: string;
  /** Code anonyme (ex. "01Z4-01") ; dans le texte on affiche [code] soit [01Z4-01] */
  anonymousId: string;
}

export interface AnonymizationResult {
  anonymizedText: string;
  replacements: {
    type: string;
    original: string;
    replacement: string;
    count: number;
  }[];
}

/**
 * Anonymise le texte en remplaçant les données sensibles
 */
export function anonymizeText(
  text: string,
  patientCorrespondence: CorrespondenceEntry[],
  practitionerNames: string[] = []
): AnonymizationResult {
  let anonymizedText = text;
  const replacements: AnonymizationResult["replacements"] = [];

  // 1. Remplacer les emails par [EMAIL]
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const emailMatches = anonymizedText.match(emailRegex) || [];
  if (emailMatches.length > 0) {
    anonymizedText = anonymizedText.replace(emailRegex, "[EMAIL]");
    replacements.push({
      type: "Email",
      original: [...new Set(emailMatches)].join(", "),
      replacement: "[EMAIL]",
      count: emailMatches.length,
    });
  }

  // 2. Remplacer les numéros de téléphone par [TELEPHONE]
  const phoneRegex = /(?:(?:\+|00)33[\s.-]?(?:\(0\)[\s.-]?)?|0)[1-9](?:[\s.-]?\d{2}){4}/g;
  const phoneMatches = anonymizedText.match(phoneRegex) || [];
  if (phoneMatches.length > 0) {
    anonymizedText = anonymizedText.replace(phoneRegex, "[TELEPHONE]");
    replacements.push({
      type: "Téléphone",
      original: [...new Set(phoneMatches)].join(", "),
      replacement: "[TELEPHONE]",
      count: phoneMatches.length,
    });
  }

  // 3. Remplacer les praticiens/docteurs par [DOCTEUR]
  const drPatterns = [
    /\bDr\.?\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+(?:[\s-]+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)*/gi,
    /\bDocteur\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+(?:[\s-]+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+)*/gi,
  ];

  for (const pattern of drPatterns) {
    const matches = anonymizedText.match(pattern) || [];
    if (matches.length > 0) {
      anonymizedText = anonymizedText.replace(pattern, "[DOCTEUR]");
      replacements.push({
        type: "Docteur",
        original: [...new Set(matches)].join(", "),
        replacement: "[DOCTEUR]",
        count: matches.length,
      });
    }
  }

  // 4. Remplacer les praticiens spécifiques (orthoptistes, etc.)
  // Ne jamais remplacer des termes courants/médicaux (forme, figure, lecture, etc.)
  const skipPractitionerTerms = new Set([
    "forme", "formes", "figure", "figures", "fond", "lecture", "ligne", "lignes", "donc", "la", "le", "les", "de", "du", "des", "pas", "bon", "perception", "capacité", "capacités", "sensorielles", "visuelle", "visuelles", "réflexe", "constance", "convergence", "vision", "analyse", "mémoire", "discrimination", "activité", "activités", "patient", "résultat", "test", "type", "thomas", "bilan", "centre", "médical", "consultation", "examen", "motilité", "poursuite", "saccades", "rdc", "ese", "ppc", "hw",
  ]);
  const normalizeTerm = (s: string) => s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").trim();

  for (const practitioner of practitionerNames) {
    if (!practitioner || practitioner.length < 2) continue;
    if (skipPractitionerTerms.has(normalizeTerm(practitioner))) continue;

    const parts = practitioner.split(/\s+/);
    const variants = [practitioner, ...parts];

    for (const variant of variants) {
      if (variant.length < 3) continue;
      if (skipPractitionerTerms.has(normalizeTerm(variant))) continue;

      const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`\\b${escaped}\\b`, "gi");
      const matches = anonymizedText.match(regex) || [];

      if (matches.length > 0) {
        anonymizedText = anonymizedText.replace(regex, "[ORTHOPTISTE]");
        replacements.push({
          type: "Orthoptiste",
          original: variant,
          replacement: "[ORTHOPTISTE]",
          count: matches.length,
        });
      }
    }
  }

  // 5. Remplacer le patient par son code anonyme
  const sortedCorrespondence = [...patientCorrespondence].sort(
    (a, b) => b.original.length - a.original.length
  );

  for (const entry of sortedCorrespondence) {
    if (!entry.original || entry.original.length < 2) continue;
    
    const escaped = entry.original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escaped}\\b`, "gi");
    const matches = anonymizedText.match(regex) || [];
    
    if (matches.length > 0) {
      anonymizedText = anonymizedText.replace(regex, `[${entry.anonymousId}]`);
      replacements.push({
        type: "Patient",
        original: entry.original,
        replacement: `[${entry.anonymousId}]`,
        count: matches.length,
      });
    }
  }

  // 6. Remplacer les adresses postales (code postal + ville)
  const postalRegex = /\b\d{5}\s+[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸŒÆÇ][a-zàâäéèêëïîôùûüÿœæç]+(?:[\s-][A-Za-zàâäéèêëïîôùûüÿœæç]+)*\b/g;
  const postalMatches = anonymizedText.match(postalRegex) || [];
  if (postalMatches.length > 0) {
    anonymizedText = anonymizedText.replace(postalRegex, "[ADRESSE]");
    replacements.push({
      type: "Adresse",
      original: [...new Set(postalMatches)].join(", "),
      replacement: "[ADRESSE]",
      count: postalMatches.length,
    });
  }

  // Consolider les remplacements
  const consolidatedReplacements = replacements.reduce((acc, curr) => {
    const existing = acc.find(
      (r) => r.type === curr.type && r.replacement === curr.replacement
    );
    if (existing) {
      existing.count += curr.count;
      if (!existing.original.includes(curr.original)) {
        existing.original += `, ${curr.original}`;
      }
    } else {
      acc.push({ ...curr });
    }
    return acc;
  }, [] as AnonymizationResult["replacements"]);

  return {
    anonymizedText,
    replacements: consolidatedReplacements,
  };
}

/** Map de désanonymisation : clé = tag (ex. [DOCTEUR]), valeur = texte original */
export type DeanonymizationMap = Record<string, string>;

/**
 * Construit la map de désanonymisation à partir des remplacements.
 * Une seule valeur par tag (la première rencontrée) pour l'affichage.
 */
export function buildDeanonymizationMap(
  replacements: AnonymizationResult["replacements"]
): DeanonymizationMap {
  const map: DeanonymizationMap = {};
  for (const r of replacements) {
    if (r.replacement && r.original && !map[r.replacement]) {
      map[r.replacement] = r.original;
    }
  }
  return map;
}

/**
 * Désanonymise un texte en remplaçant les tags par les valeurs originales.
 * Les clés les plus longues sont traitées en premier pour éviter les remplacements partiels.
 */
export function deanonymizeText(
  text: string,
  map: DeanonymizationMap | null | undefined
): string {
  if (!text) return text;
  if (!map || typeof map !== "object" || Object.keys(map).length === 0) return text;

  let result = text;
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const value = map[key];
    if (value != null && key) {
      result = result.split(key).join(value);
    }
  }
  return result;
}
